
import { useState, useEffect } from 'react';

interface NavbarProps {
  onNavigate: (sectionId: string) => void;
}

export default function Navbar({ onNavigate }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = [
        'home',
        'filosofia',
        'storia',
        'progetti',
        'statistiche',
        'passione',
        'visione',
      ];

      const current = sections.find((section) => {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          return rect.top <= 120 && rect.bottom >= 120;
        }
        return false;
      });

      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navItems = [
    { id: 'home', label: 'Incitti' },
    { id: 'filosofia', label: 'Filosofia' },
    { id: 'storia', label: 'La Mia Storia' },
    { id: 'progetti', label: 'Progetti' },
    { id: 'passione', label: 'Passione' },
    { id: 'visione', label: 'Visione' },
  ];

  const handleNav = (id: string) => {
    setMobileOpen(false);
    onNavigate(id);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen
          ? 'py-3 bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.06)]'
          : 'py-4 md:py-5 bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 flex items-center justify-between">
        <button
          onClick={() => handleNav('home')}
          className="flex items-center gap-2 cursor-pointer group"
        >
          <span
            className={`font-playfair text-xl md:text-2xl font-bold tracking-tight transition-colors duration-500 ${
              scrolled || mobileOpen ? 'text-[#2d3e40]' : 'text-white'
            }`}
          >
            Marco Incitti
          </span>
          <span
            className={`font-montserrat text-[9px] md:text-[10px] uppercase tracking-[3px] mt-1 transition-colors duration-500 ${
              scrolled || mobileOpen ? 'text-[#6b8e7f]' : 'text-white/70'
            }`}
          >
            Architetto
          </span>
        </button>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNav(item.id)}
              className={`font-montserrat text-[13px] tracking-wide transition-all duration-400 cursor-pointer whitespace-nowrap relative ${
                activeSection === item.id
                  ? scrolled
                    ? 'text-[#6b8e7f] font-semibold'
                    : 'text-white font-semibold'
                  : scrolled
                  ? 'text-[#2d3e40]/70 hover:text-[#6b8e7f]'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              {item.label}
              {activeSection === item.id && (
                <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-[#6b8e7f] rounded-full"></span>
              )}
            </button>
          ))}
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          aria-label="Menu"
        >
          <div className="flex flex-col gap-[5px]">
            <span className={`block w-6 h-[2px] transition-all duration-300 ${
              mobileOpen ? 'rotate-45 translate-y-[7px] bg-[#2d3e40]' : scrolled ? 'bg-[#2d3e40]' : 'bg-white'
            }`}></span>
            <span className={`block w-6 h-[2px] transition-all duration-300 ${
              mobileOpen ? 'opacity-0' : scrolled ? 'bg-[#2d3e40]' : 'bg-white'
            }`}></span>
            <span className={`block w-6 h-[2px] transition-all duration-300 ${
              mobileOpen ? '-rotate-45 -translate-y-[7px] bg-[#2d3e40]' : scrolled ? 'bg-[#2d3e40]' : 'bg-white'
            }`}></span>
          </div>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden transition-all duration-400 overflow-hidden ${
        mobileOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-5 pt-4 pb-6 flex flex-col gap-1 bg-white/95 backdrop-blur-md">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNav(item.id)}
              className={`font-montserrat text-[15px] py-3 px-4 rounded-lg text-left cursor-pointer transition-colors ${
                activeSection === item.id
                  ? 'text-[#6b8e7f] font-semibold bg-[#6b8e7f]/5'
                  : 'text-[#2d3e40]/70 hover:text-[#6b8e7f] hover:bg-[#6b8e7f]/5'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}
